import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PackageSearch, Search, Loader2, ClipboardCheck, X, CheckCircle, Settings } from 'lucide-react';
import { DateInput } from '@/components/ui/date-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const FRESH_DOT = { fresh: 'bg-green-500', warning: 'bg-yellow-500', expired: 'bg-red-500', never: 'bg-zinc-500' };
const BADGE_CLS = {
  'In Stock': 'bg-green-500/15 text-green-300 border-green-500/40',
  'Low Stock': 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  'Out of Stock': 'bg-red-500/15 text-red-300 border-red-500/40',
  'Made On Order': 'bg-violet-500/15 text-violet-300 border-violet-500/40',
  'Marketplace': 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40',
  'Need Vendor Check': 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  'Price Changed': 'bg-pink-500/15 text-pink-300 border-pink-500/40',
};
const bcls = (b) => BADGE_CLS[b] || 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40';

const CheckDialog = ({ open, onOpenChange, sku, vendors, onSaved }) => {
  const [f, setF] = useState({ vendor_id: '', checked_price: '', estimated_stock: '', availability_status: 'available', lead_time_text: '', valid_until: '', notes: '', source: 'whatsapp', is_price_confirmed: false });
  const [saving, setSaving] = useState(false);
  const [allVendors, setAllVendors] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/availability/vendors`, auth())
      .then(r => setAllVendors(r.data || []))
      .catch(() => setAllVendors([]));
  }, [open]);

  const mappedIds = (vendors || []).map(v => v.vendor_id);
  const otherVendors = allVendors.filter(v => !mappedIds.includes(v.vendor_id));

  const save = async () => {
    if (!f.vendor_id) { toast.error('Select a vendor'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/availability/checks`, { ...f, sku }, auth());
      toast.success(r.data.message);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save check'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-wt-surface border-wt-border max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-wt-text-primary">Add Availability Check — {sku}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm text-wt-text-primary">Vendor *</Label>
            <Select value={f.vendor_id} onValueChange={(v) => setF(p => ({ ...p, vendor_id: v }))}>
              <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="check-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border max-h-72">
                {(vendors || []).length > 0 && (
                  <div className="px-2 py-1 text-[10px] uppercase text-wt-text-muted">Linked vendors</div>
                )}
                {(vendors || []).map((v, i) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name || `Vendor ${i + 1}`}</SelectItem>)}
                {otherVendors.length > 0 && (
                  <div className="px-2 py-1 text-[10px] uppercase text-wt-text-muted">All vendors</div>
                )}
                {otherVendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-sm text-wt-text-primary">Checked Price</Label>
              <Input type="number" value={f.checked_price} onChange={(e) => setF(p => ({ ...p, checked_price: e.target.value }))} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="check-price-input" /></div>
            <div><Label className="text-sm text-wt-text-primary">Est. Stock</Label>
              <Input type="number" value={f.estimated_stock} onChange={(e) => setF(p => ({ ...p, estimated_stock: e.target.value }))} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-sm text-wt-text-primary">Status *</Label>
              <Select value={f.availability_status} onValueChange={(v) => setF(p => ({ ...p, availability_status: v }))}>
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="check-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {[['available', 'Available'], ['limited_stock', 'Limited Stock'], ['not_available', 'Not Available'], ['made_on_order', 'Made On Order'], ['need_confirmation', 'Need Confirmation']].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-sm text-wt-text-primary">Source</Label>
              <Select value={f.source} onValueChange={(v) => setF(p => ({ ...p, source: v }))}>
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {['whatsapp', 'call', 'email', 'vendor_portal', 'manual'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-sm text-wt-text-primary">Lead Time</Label>
              <Input placeholder="e.g. 2-3 days" value={f.lead_time_text} onChange={(e) => setF(p => ({ ...p, lead_time_text: e.target.value }))} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" /></div>
            <div><Label className="text-sm text-wt-text-primary">Valid Until</Label>
              <DateInput  value={f.valid_until} onChange={(e) => setF(p => ({ ...p, valid_until: e.target.value }))} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" /></div>
          </div>
          <div><Label className="text-sm text-wt-text-primary">Notes</Label>
            <Textarea rows={2} value={f.notes} onChange={(e) => setF(p => ({ ...p, notes: e.target.value }))} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" /></div>
          <label className="flex items-center gap-2 text-sm text-wt-text-primary">
            <input type="checkbox" checked={f.is_price_confirmed} onChange={(e) => setF(p => ({ ...p, is_price_confirmed: e.target.checked }))} /> Price confirmed by vendor
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="save-check-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save Check
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ProductAvailabilityPage() {
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') || '');
  const [filter, setFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [checkSku, setCheckSku] = useState(null);
  const [checkVendors, setCheckVendors] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ fresh_hours: 24, warning_hours: 72 });
  const [savingSettings, setSavingSettings] = useState(false);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/availability/settings`, settingsForm, auth());
      toast.success('Freshness settings saved');
      setSettingsOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save settings'); }
    finally { setSavingSettings(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/availability/products`, { ...auth(), params: { search: search || undefined, filter: filter === 'all' ? undefined : filter } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [search, filter]);

  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

  const openDetail = async (sku) => {
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/availability/product/${encodeURIComponent(sku)}`, auth());
      setDetail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load detail'); }
    finally { setDetailLoading(false); }
  };

  const approvePrice = async (checkId) => {
    try {
      const r = await axios.post(`${API}/availability/checks/${checkId}/approve-price`, {}, auth());
      toast.success(r.data.message);
      openDetail(detail.product.sku);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="product-availability-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-wt-text-primary flex items-center gap-2">
            <PackageSearch className="w-7 h-7 text-wt-gold" /> Product Availability
          </h1>
          <p className="text-sm text-wt-text-muted">Is it available, from where, at what cost, and how fast?</p>
        </div>
        {data?.permissions?.can_manage_settings && (
          <Button variant="outline" size="sm" className="border-wt-border text-wt-text-primary"
            data-testid="availability-settings-btn"
            onClick={() => {
              setSettingsForm({
                fresh_hours: data?.settings?.fresh_hours ?? 24,
                warning_hours: data?.settings?.warning_hours ?? 72,
              });
              setSettingsOpen(true);
            }}>
            <Settings className="w-4 h-4 mr-1.5" /> Settings
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wt-text-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product name or SKU..."
            className="pl-9 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="availability-search" />
        </div>
        {[['all', 'All'], ['in_stock', 'In Stock'], ['out_of_stock', 'Out of Stock'], ['made_on_order', 'Made On Order'], ['marketplace', 'Marketplace']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`availability-filter-${v}`}
            className={`px-3 py-1.5 rounded-full text-xs border ${filter === v ? 'bg-wt-gold/20 text-wt-gold border-wt-gold/50' : 'bg-wt-bg text-wt-text-muted border-wt-border'}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-wt-gold" /></div>
      ) : (
        <div className="space-y-2">
          {(data?.rows || []).map(row => (
            <div key={row.sku} className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-wt-surface border border-wt-border" data-testid={`availability-row-${row.sku}`}>
              {row.image_url
                ? <img src={row.image_url} alt="" className="w-11 h-11 rounded object-cover flex-shrink-0" />
                : <div className="w-11 h-11 rounded bg-wt-paper flex-shrink-0" />}
              <div className="min-w-0 w-64">
                <p className="text-sm text-wt-text-primary truncate">{row.name}{row.variant_title ? ` — ${row.variant_title}` : ''}</p>
                <p className="text-xs text-wt-text-muted">SKU: {row.sku}</p>
              </div>
              <div className="text-xs text-wt-text-muted">
                <p>Stock: <span className={row.available > 0 ? 'text-green-400' : 'text-red-400'}>{row.available}</span> avail{row.blocked ? ` (${row.blocked} blocked)` : ''}{row.reserved ? ` (${row.reserved} reserved)` : ''}</p>
                <p>Vendors: {row.vendor_count}</p>
              </div>
              <div className="flex flex-wrap gap-1 max-w-xs">
                {row.badges.slice(0, 4).map(b => <Badge key={b} className={`text-[10px] border ${bcls(b)}`}>{b}</Badge>)}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-wt-text-muted">
                <span className={`w-2 h-2 rounded-full ${FRESH_DOT[row.freshness.status]}`} />
                {row.freshness.status === 'never' ? 'Never checked' : row.freshness.label.split(' — ')[0]}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-wt-gold hidden md:block max-w-[180px] truncate" title={row.recommended.label}>{row.recommended.label}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs border-wt-border text-wt-text-primary"
                  onClick={() => openDetail(row.sku)} data-testid={`view-availability-${row.sku}`}>View</Button>
                <Button size="sm" className="h-7 text-xs bg-wt-gold/20 text-wt-gold border border-wt-gold/40 hover:bg-wt-gold/30"
                  onClick={() => { setCheckSku(row.sku); setCheckVendors(row.vendors); setCheckOpen(true); }} data-testid={`add-check-${row.sku}`}>
                  <ClipboardCheck className="w-3 h-3 mr-1" /> Check
                </Button>
              </div>
            </div>
          ))}
          {!data?.rows?.length && <p className="text-center text-wt-text-muted py-10">No products found</p>}
        </div>
      )}

      {/* Detail drawer */}
      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl h-full bg-wt-bg border-l border-wt-border overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="availability-detail-panel">
            {detailLoading || !detail ? <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-wt-gold" /></div> : (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-3">
                    {detail.product.image_url && <img src={detail.product.image_url} alt="" className="w-16 h-16 rounded object-cover" />}
                    <div>
                      <h2 className="text-lg font-bold text-wt-text-primary">{detail.product.name}</h2>
                      <p className="text-xs text-wt-text-muted">SKU: {detail.product.sku} · Stock: {detail.product.available} available ({detail.product.blocked || 0} blocked, {detail.product.reserved} reserved)</p>
                      <div className="flex flex-wrap gap-1 mt-1">{detail.product.badges.map(b => <Badge key={b} className={`text-[10px] border ${bcls(b)}`}>{b}</Badge>)}</div>
                    </div>
                  </div>
                  <button onClick={() => setDetail(null)} className="text-wt-text-muted" data-testid="close-detail-btn"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-3 rounded-lg bg-wt-gold/10 border border-wt-gold/40 mb-4" data-testid="sales-summary-box">
                  <p className="text-xs font-semibold text-wt-gold mb-1">Recommended source: {detail.recommended.label}{detail.recommended.lead_time ? ` · ${detail.recommended.lead_time}` : ''}</p>
                  {detail.summary_lines.map((l, i) => <p key={i} className="text-xs text-wt-text-secondary">• {l}</p>)}
                  <p className="text-sm text-wt-text-primary mt-2 italic">"{detail.recommended_reply}"</p>
                </div>

                <h3 className="text-sm font-semibold text-wt-text-primary mb-2">Vendors ({detail.vendors.length})</h3>
                <div className="space-y-2 mb-4">
                  {detail.vendors.map((v, i) => (
                    <div key={i} className="p-3 rounded-lg bg-wt-surface border border-wt-border text-xs" data-testid={`vendor-row-${i}`}>
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <p className="text-sm text-wt-text-primary font-medium">
                          {v.vendor_name} {v.is_primary && <span className="text-wt-gold">(primary)</span>} {v.is_preferred && <span className="text-green-400">(preferred)</span>}
                          <span className="text-wt-text-muted capitalize ml-2">{v.vendor_type}</span>
                        </p>
                        <span className="flex items-center gap-1 text-wt-text-muted">
                          <span className={`w-2 h-2 rounded-full ${FRESH_DOT[v.check_freshness.status]}`} />{v.check_freshness.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 mt-1 text-wt-text-muted">
                        {'cost_price' in v && <p>Cost: <span className="text-wt-text-primary">{fmt(v.cost_price)}</span></p>}
                        {'checked_price' in v && v.checked_price != null && <p>Checked price: <span className={v.cost_delta ? 'text-pink-400' : 'text-wt-text-primary'}>{fmt(v.checked_price)}{v.cost_delta ? ` (${v.cost_delta > 0 ? '+' : ''}${fmt(v.cost_delta)})` : ''}</span></p>}
                        {'last_purchase_date' in v && <p>Last PO: {v.last_po_number || '—'} {v.last_purchase_date ? `(${String(v.last_purchase_date).slice(0, 10)})` : ''}</p>}
                        {'last_purchase_cost' in v && v.last_purchase_cost != null && <p>Last buy cost: {fmt(v.last_purchase_cost)} × {v.last_purchase_qty || '—'}</p>}
                        <p>Status: <span className="capitalize text-wt-text-primary">{(v.status || '').replace(/_/g, ' ')}</span></p>
                        <p>Lead: {(v.latest_check || {}).lead_time_text || (v.lead_time_days ? `${v.lead_time_days} days` : '—')}</p>
                      </div>
                      {v.latest_check?.price_changed && v.latest_check?.price_update_status !== 'approved' && detail.permissions.can_view_cost && (
                        <Button size="sm" className="h-6 mt-2 text-[11px] bg-pink-500/15 text-pink-300 border border-pink-500/40"
                          onClick={() => approvePrice(v.latest_check.id)} data-testid={`approve-price-${i}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Approve new cost {fmt(v.latest_check.checked_price)}
                        </Button>
                      )}
                    </div>
                  ))}
                  {!detail.vendors.length && <p className="text-xs text-wt-text-muted">No vendors mapped to this product</p>}
                </div>

                <div className="flex gap-2 mb-4">
                  <Button size="sm" className="bg-wt-gold/20 text-wt-gold border border-wt-gold/40 hover:bg-wt-gold/30"
                    onClick={() => { setCheckSku(detail.product.sku); setCheckVendors(detail.vendors); setCheckOpen(true); }} data-testid="detail-add-check-btn">
                    <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Add Availability Check
                  </Button>
                </div>

                <h3 className="text-sm font-semibold text-wt-text-primary mb-2">Availability History</h3>
                <div className="space-y-1.5">
                  {detail.history.map(h => (
                    <div key={h.id} className="text-[11px] text-wt-text-muted border-l-2 border-wt-border pl-2">
                      {String(h.checked_at).slice(0, 16).replace('T', ' ')} — {h.vendor_name || 'Vendor'} · <span className="capitalize">{(h.availability_status || '').replace(/_/g, ' ')}</span>
                      {'checked_price' in h && h.checked_price != null ? ` · ${fmt(h.checked_price)}` : ''}
                      {h.estimated_stock != null ? ` · ~${h.estimated_stock} pcs` : ''}
                      {h.lead_time_text ? ` · ${h.lead_time_text}` : ''} · by {h.checked_by_name} ({h.source})
                      {h.notes ? ` — ${h.notes}` : ''}
                    </div>
                  ))}
                  {!detail.history.length && <p className="text-xs text-wt-text-muted">No manual checks yet</p>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <CheckDialog open={checkOpen} onOpenChange={setCheckOpen} sku={checkSku} vendors={checkVendors}
        onSaved={() => { load(); if (detail) openDetail(detail.product.sku); }} />

      {/* Freshness settings (availability.manage_settings) */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-sm">
          <DialogHeader><DialogTitle className="text-wt-text-primary">Availability Settings</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm text-wt-text-primary">Fresh check window (hours)</Label>
              <Input type="number" value={settingsForm.fresh_hours}
                onChange={(e) => setSettingsForm(p => ({ ...p, fresh_hours: parseFloat(e.target.value) || 0 }))}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="fresh-hours-input" />
              <p className="text-[11px] text-wt-text-muted mt-1">Checks newer than this show a green dot (reliable)</p>
            </div>
            <div>
              <Label className="text-sm text-wt-text-primary">Warning window (hours)</Label>
              <Input type="number" value={settingsForm.warning_hours}
                onChange={(e) => setSettingsForm(p => ({ ...p, warning_hours: parseFloat(e.target.value) || 0 }))}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="warning-hours-input" />
              <p className="text-[11px] text-wt-text-muted mt-1">Older checks show yellow; beyond this they show red — "needs reconfirmation before promising customer"</p>
            </div>
            <p className="text-[11px] text-wt-text-muted">This permission also allows approving checked prices as the new vendor cost and setting a preferred vendor per product.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Cancel</Button>
              <Button onClick={saveSettings} disabled={savingSettings} className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="save-availability-settings-btn">
                {savingSettings && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
